import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { loginUser, clearError } from "../store/authSlice";
import { LoginRequest } from "../types/auth";

import ForgotPasswordForm from "./ForgotPasswordForm";
import ResetPasswordForm from "./ResetPasswordForm";

interface LogInProps {
  onLoginSuccess?: () => void;
  onShowSignUp?: () => void;
}

const LogIn: React.FC<LogInProps> = ({ onLoginSuccess, onShowSignUp }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isError, setIsError] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        navigate("/leads");
      }
    }
  }, [isAuthenticated, onLoginSuccess, navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const loginData: LoginRequest = {
      email,
      password,
    };

    try {
      await dispatch(loginUser(loginData)).unwrap();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const isButtonDisabled = !email || !password;


  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setShowResetPassword(false);
    window.history.replaceState(null, "", window.location.pathname);
  };

  const handleShowResetForm = () => {
    setShowResetPassword(true);
    window.history.replaceState(
      null,
      "",
      window.location.pathname + "#reset-password"
    );
  };


  if (showResetPassword) {
    return (
      <ResetPasswordForm
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  if (showForgotPassword) {
    return (
      <ForgotPasswordForm
        onBackToLogin={handleBackToLogin}
        onShowResetForm={handleShowResetForm}
      />
    );
  }

  return (
    <div className="flex justify-center items-center login-form-width">
      <div className="w-[444px] p-6 rounded-2xl border border-lighterGray login-box-shadow h-fit">
        <h2 className="text-h1 font-bold text-darkestSlate pb-2">Log In</h2>
        <div className="flex items-center gap-1">
          <span className="text-h3 text-darkGray">Don't have an account?</span>
          <button
            type="button"
            className="text-h3 font-semibold text-darkBlue hover:underline"
            onClick={onShowSignUp}
          >
            Sign Up
          </button>
        </div>
        <div className="border-b border-lighterGray my-8"></div>
        <form className="flex flex-col" onSubmit={handleSubmit}>
          <label
            htmlFor="form-mail"
            className="text-h4 font-medium text-darkGray pb-1"
          >
            Email *
          </label>
          <input
            type="email"
            name="email"
            placeholder="Email"
            id="form-mail"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setIsError(false);
            }}
            className={`h-10 px-3 py-2.5 mb-1 rounded-xl bg-lightestGray border ${
              isError || error ? "input-error" : "border-lighterGray"
            }`}
          />
          {(isError || error) && <p className="input-error-text text-sm">{error || "Error"}</p>}
          <label
            htmlFor="form-pass"
            className="text-h4 font-medium text-darkGray pb-1 mt-3"
          >
            Password *
          </label>
          <div className="relative mb-4">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              id="form-pass"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setIsError(false);
              }}
              className={`h-10 px-3 py-2.5 rounded-xl bg-lightestGray border w-full ${
                isError || error ? "input-error" : "border-lighterGray"
              }`}
            />
            <span
              className="absolute input-icon cursor-pointer"
              onClick={togglePasswordVisibility}
            >
              <i
                className={`fa-regular ${
                  showPassword ? "fa-eye" : "fa-eye-slash"
                } text-darkGray`}
              ></i>
            </span>
          </div>
          <button
            type="button"
            className="text-h3 font-semibold text-darkBlue hover:underline mb-6 text-start"
            onClick={() => {
              setShowForgotPassword(true);
            }}
          >
            Forgot Password?
          </button>
          <button
            disabled={isButtonDisabled || isLoading}
            type="submit"
            className={`text-h3 font-semibold text-lightestGray h-10 rounded-3xl hover:bg-mediumGray ${
              isButtonDisabled || isLoading ? "bg-gray cursor-not-allowed" : "bg-darkBlue"
            }`}
          >
            {isLoading ? "Logging in..." : "Log in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LogIn;
